import { useEffect, useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Mail, User, Lock } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Input } from "@shared/components/ui/input";
import { PhoneInput } from "@shared/components/common/PhoneInput";
import BookingHeader from "@features/booking/components/BookingHeader";
import { BookingChrome } from "@features/booking/layouts";
import BookingFooter from "@features/booking/components/BookingFooter";
import { useAuth } from "@core/auth";
import { registerAndLogin } from "@core/auth/authApi";
import { ApiError } from "@core/api";
import { useTenant } from "@core/tenant";
import { useBookingFlow } from "@features/booking/flow";
import { useBooking } from "@features/booking/state";
import { getCurrentLanguage } from "@core/i18n";
import { safeSessionStorage } from "@shared/utils/safeStorage";
import { isPhoneEmpty, isValidPhone } from "@shared/utils/phoneValidation";

const MIN_PASSWORD_LENGTH = 8;

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const { loginWithToken, authState } = useAuth();
  const { subdomainPrefix } = useTenant();
  const { activeSteps } = useBookingFlow();
  const { selectedDate, selectedTime } = useBooking();

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const [searchParams] = useSearchParams();
  // Shows a contextual banner when the user lands here after a failed
  // cross-subdomain login (AuthReturnGate redirects with this query param).
  // Captured once on mount so the banner survives AuthErrorToast stripping
  // the param from the URL.
  const fallbackBanner = useMemo(
    () => searchParams.get('auth_error') === 'storage_write_failed',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Post-auth redirect mirrors SignInPage so both pages behave identically:
  //   1. explicit returnTo → honor it
  //   2. user is mid-booking (date + time picked) → continue to next step
  //   3. neither → home, since a direct visit to /sign-up has no flow to resume
  useEffect(() => {
    if (!authState.isAuthenticated) return;
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    const inBookingFlow = !!selectedDate && !!selectedTime;
    if (inBookingFlow) {
      const timeIdx = activeSteps.findIndex((s) => s.id === "TIME");
      const nextStep = activeSteps[timeIdx + 1];
      navigate(nextStep?.path || "/personalize", { replace: true });
      return;
    }
    navigate("/", { replace: true });
  }, [authState.isAuthenticated, activeSteps, returnTo, selectedDate, selectedTime, navigate]);

  const defaultCountry = safeSessionStorage.getItem("restaurant_country") || "";
  const dialCode = safeSessionStorage.getItem("restaurant_dial_code") || "";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const handleSignIn = () => {
    navigate("/sign-in", { state: { returnTo } });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setPhoneError(null);

      if (!name.trim() || !email.trim() || !password.trim()) {
        setError(t("FORM_ERROR_FILL_REQUIRED"));
        return;
      }

      if (password.trim().length < MIN_PASSWORD_LENGTH) {
        setError(t("FORM_ERROR_INVALID_PASSWORD"));
        return;
      }

      if (isPhoneEmpty(phone)) {
        setPhoneError(t("FORM_ERROR_TYPE_PHONE"));
        return;
      }

      if (!isValidPhone(phone)) {
        setPhoneError(t("FORM_ERROR_INVALID_PHONE"));
        return;
      }

      setIsSubmitting(true);
      // Track which side raised so a post-registration session failure does
      // not look like a registration error to the user.
      let registrationDone = false;
      try {
        const lang = getCurrentLanguage();
        const result = await registerAndLogin(lang, {
          username: email.trim(),
          name: name.trim(),
          password,
          phone: phone.trim(),
          restaurant: subdomainPrefix || "",
          optInEmail: marketingConsent,
        });
        registrationDone = true;
        await loginWithToken(result.token, result.email);
        // loginWithToken flips authState.isAuthenticated; the effect above
        // handles navigation. No explicit navigate() here to avoid racing the
        // effect and double-navigating.
      } catch (err) {
        if (registrationDone) {
          // Registration succeeded but session establishment failed (most
          // commonly auth.token_write_readback_mismatch from
          // setAuthTokenWithReadback in WebViews / private mode). Showing
          // SIGNUP_REGISTRATION_ERROR here would mislead the user into
          // retrying registration and hitting EMAIL_ALREADY_EXISTS.
          setError(t("MAGIC_LINK_GENERIC_ERROR"));
        } else if (err instanceof ApiError && typeof err.serverCode === "string") {
          const key = "SIGNUP_ERROR_" + err.serverCode.toUpperCase();
          setError(t(key, { defaultValue: t("SIGNUP_REGISTRATION_ERROR") }));
        } else if (err instanceof ApiError) {
          setError(err.message || t("SIGNUP_REGISTRATION_ERROR"));
        } else {
          setError(t("SIGNUP_REGISTRATION_ERROR"));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, email, password, phone, marketingConsent, subdomainPrefix, loginWithToken, t],
  );

  return (
    <BookingChrome>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center">
          {fallbackBanner && (
            <div
              role="status"
              className="w-full max-w-xs mb-6 rounded-md border border-brand/40 bg-brand/5 px-4 py-3 text-sm text-foreground/80"
            >
              {t("AUTH_FALLBACK_AFTER_FAILURE")}
            </div>
          )}
          <form onSubmit={handleSubmit} className="w-full max-w-xs flex flex-col gap-5">
            <h1 className="text-3xl font-display mb-1 text-center">
              {t("SIGNUP_FORM_TITLE")}
            </h1>
            <p className="text-foreground/50 text-sm mb-2 text-center">
              {t("SIGNUP_FORM_SUBTITLE")}
            </p>

            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
              <Input
                className="pl-10 py-6"
                placeholder={t("FORM_LABEL_NAME")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                type="text"
              />
            </div>

            <PhoneInput
              value={phone}
              onChange={(v) => {
                setPhone(v);
                if (!phoneError) return;
                if (isPhoneEmpty(v)) setPhoneError(t("FORM_ERROR_TYPE_PHONE"));
                else if (!isValidPhone(v)) setPhoneError(t("FORM_ERROR_INVALID_PHONE"));
                else setPhoneError(null);
              }}
              defaultCountry={defaultCountry}
              dialCode={dialCode}
              className={`rounded-md border bg-background px-3 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${phoneError ? "border-red-500" : "border-input"}`}
            />

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
              <Input
                className="pl-10 py-6"
                placeholder={t("SIGNUP_LABEL_EMAIL")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                type="email"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" size={20} />
              <Input
                className="pl-10 py-6"
                placeholder={t("SIGNUP_LABEL_PASSWORD")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                type="password"
              />
            </div>

            <label className="flex items-start gap-3 text-foreground/70 text-sm mt-1 mb-2 cursor-pointer select-none">
              <Checkbox
                checked={marketingConsent}
                onCheckedChange={() => setMarketingConsent((v) => !v)}
                className="mt-0.5 rounded"
              />
              <span>{t("NEW_MODAL_AGREE_RESTAURANT_SEND_OCCASIONAL_INFO")}</span>
            </label>

            {(error || phoneError) && (
              <p className="text-red-500 text-sm text-center">{error || phoneError}</p>
            )}

            <Button
              type="submit"
              className="h-12 px-8 rounded-xl shadow-md flex items-center justify-center gap-2 w-full transition-all duration-200 hover:scale-[1.02]"
              disabled={isSubmitting}
              aria-label={t("SIGNUP_LABEL_SUBMIT")}
            >
              <Mail className="w-5 h-5 mr-2" />
              {isSubmitting ? t("MAGIC_LINK_SENDING") : t("SIGNUP_LABEL_SUBMIT")}
            </Button>

            <div className="w-full text-center mt-3">
              <span className="text-foreground/70 text-sm">
                {t("NEW_MODAL_ALREADY_HAVE_ACCOUNT")}{" "}
                <button
                  type="button"
                  className="text-brand font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
                  onClick={handleSignIn}
                >
                  {t("SIGNUP_LOG_IN")}
                </button>
              </span>
            </div>
          </form>
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back to previous page"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm mr-4"
              onClick={handleBack}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default SignUp;
